import React, { useRef, useEffect, memo } from 'react';
import { TableBody, TableRow, TableCell, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { flexRender } from '@tanstack/react-table';
import type { Table, VisibilityState } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { handleDataGridRowClick } from './dataGridRowClick';
import { getStripedRowBackground, getUnreadRowBackground } from './rowBackgroundStyles';
import { getColumnWidth } from '../utils';
import { isBooleanValueTrue } from '../../../utils';

// Render all rows directly when the count is below this threshold (no virtualizer overhead).
const VIRTUALIZATION_THRESHOLD = 100;

/** Unread only when IsRead is explicitly present and false (other grids omit the field). */
const isUnreadGridRow = (row: Record<string, unknown>): boolean => {
  const value = row.IsRead;
  if (value === undefined || value === null || value === '') {
    return false;
  }

  return !isBooleanValueTrue(value);
};

type DataGridBodyProps<T> = {
  focusRowId?: string;
  table: Table<T>;
  modifiedCells?: Set<string>;
  markedForDeletion?: Set<string>;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  rowHeight?: number;
  wordWrap?: boolean;
  hiddenColumnIds?: Set<string>;
};

/**
 * DataGrid table body component with row virtualisation.
 *
 * For grids with < VIRTUALIZATION_THRESHOLD rows all rows are rendered directly.
 * For larger datasets @tanstack/react-virtual is used so only the visible rows
 * (plus overscan) exist in the DOM — main-thread cost is proportional to the
 * viewport size, not the dataset size.
 */
export const DataGridBody = <T extends Record<string, unknown>>({
  focusRowId,
  table,
  modifiedCells,
  markedForDeletion,
  scrollContainerRef,
  rowHeight = 32,
  wordWrap = false,
  hiddenColumnIds,
}: DataGridBodyProps<T>) => {
  const rows = table.getRowModel().rows;

  const colVis = table.getState().columnVisibility;
  const colOrder = table.getState().columnOrder;

  // ─── Empty state ─────────────────────────────────────────────────────────
  if (rows.length === 0) {
    return (
      <TableBody>
        <TableRow sx={{ height: '100%' }}>
          <TableCell
            colSpan={
              table.getVisibleLeafColumns().filter((col) => !hiddenColumnIds?.has(col.id)).length
            }
            sx={{ textAlign: 'center', py: 4, height: '100%' }}
          >
            <Typography color="text.secondary">No results found</Typography>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  // ─── Small datasets: render all rows directly ────────────────────────────
  if (rows.length < VIRTUALIZATION_THRESHOLD) {
    return (
      <TableBody>
        {rows.map((row, rowIndex) => (
          <DataRow
            key={row.id}
            row={row}
            rowIndex={rowIndex}
            isSelected={row.getIsSelected()}
            columnVisibility={colVis}
            columnOrder={colOrder}
            modifiedCells={modifiedCells}
            markedForDeletion={markedForDeletion}
            validationErrors={table.options.meta?.validationErrors}
            table={table}
            rowHeight={rowHeight}
            wordWrap={wordWrap}
            hiddenColumnIds={hiddenColumnIds}
          />
        ))}
      </TableBody>
    );
  }

  // ─── Large datasets: virtual scroll ──────────────────────────────────────
  return (
    <VirtualBody
      focusRowId={focusRowId}
      rows={rows}
      table={table}
      columnVisibility={colVis}
      columnOrder={colOrder}
      modifiedCells={modifiedCells}
      markedForDeletion={markedForDeletion}
      scrollContainerRef={scrollContainerRef}
      rowHeight={rowHeight}
      wordWrap={wordWrap}
      hiddenColumnIds={hiddenColumnIds}
    />
  );
};

// ─── VirtualBody ─────────────────────────────────────────────────────────────

type VirtualBodyProps<T> = {
  focusRowId?: string;
  rows: ReturnType<Table<T>['getRowModel']>['rows'];
  table: Table<T>;
  columnVisibility: VisibilityState;
  columnOrder: string[];
  modifiedCells?: Set<string>;
  markedForDeletion?: Set<string>;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  rowHeight?: number;
  wordWrap?: boolean;
  hiddenColumnIds?: Set<string>;
};

const VirtualBody = <T extends Record<string, unknown>>({
  focusRowId,
  rows,
  table,
  columnVisibility,
  columnOrder,
  modifiedCells,
  markedForDeletion,
  scrollContainerRef,
  rowHeight = 32,
  wordWrap = false,
  hiddenColumnIds,
}: VirtualBodyProps<T>) => {
  const localRef = useRef<HTMLDivElement | null>(null);

  // TanStack Virtual returns imperative functions that React Compiler cannot memoize safely.
  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current ?? localRef.current,
    estimateSize: () => rowHeight + 1,
    overscan: 15,
  });

  useEffect(() => {
    if (!focusRowId) return;
    const index = rows.findIndex((row) => String(row.original.Id) === focusRowId);
    if (index >= 0) virtualizer.scrollToIndex(index, { align: 'center' });
  }, [focusRowId, rows, virtualizer]);

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0 ? totalSize - virtualRows[virtualRows.length - 1].end : 0;

  return (
    <TableBody>
      {paddingTop > 0 && (
        <TableRow style={{ height: paddingTop }}>
          <TableCell
            colSpan={
              table.getVisibleLeafColumns().filter((col) => !hiddenColumnIds?.has(col.id)).length
            }
            style={{ padding: 0, border: 'none' }}
          />
        </TableRow>
      )}

      {virtualRows.map((virtualRow) => {
        const row = rows[virtualRow.index];
        return (
          <DataRow
            key={row.id}
            row={row}
            rowIndex={virtualRow.index}
            isSelected={row.getIsSelected()}
            columnVisibility={columnVisibility}
            columnOrder={columnOrder}
            modifiedCells={modifiedCells}
            markedForDeletion={markedForDeletion}
            validationErrors={table.options.meta?.validationErrors}
            table={table}
            rowHeight={rowHeight}
            wordWrap={wordWrap}
            rowRef={virtualizer.measureElement}
            virtualIndex={virtualRow.index}
            hiddenColumnIds={hiddenColumnIds}
          />
        );
      })}

      {paddingBottom > 0 && (
        <TableRow style={{ height: paddingBottom }}>
          <TableCell
            colSpan={
              table.getVisibleLeafColumns().filter((col) => !hiddenColumnIds?.has(col.id)).length
            }
            style={{ padding: 0, border: 'none' }}
          />
        </TableRow>
      )}
    </TableBody>
  );
};

// ─── DataRow (Memoized) ──────────────────────────────────────────────────────

type DataRowProps<T> = {
  row: ReturnType<Table<T>['getRowModel']>['rows'][number];
  table: Table<T>;
  columnVisibility: VisibilityState;
  columnOrder: string[];
  isSelected: boolean;
  modifiedCells?: Set<string>;
  markedForDeletion?: Set<string>;
  validationErrors?: Map<string, string>;
  rowHeight?: number;
  wordWrap?: boolean;
  rowRef?: React.Ref<HTMLTableRowElement>;
  virtualIndex?: number;
  rowIndex: number;
  hiddenColumnIds?: Set<string>;
};

const DataRowComponent = <T extends Record<string, unknown>>({
  row,
  table,
  isSelected,
  modifiedCells,
  markedForDeletion,
  validationErrors,
  rowHeight = 32,
  wordWrap = false,
  rowRef,
  virtualIndex,
  rowIndex,
  hiddenColumnIds,
}: DataRowProps<T>) => {
  const rowId = String(row.original.Id || '');
  const isNewRow = rowId.startsWith('temp_');
  const isMarkedForDeletion = markedForDeletion?.has(rowId) ?? false;
  const isSoftDeleted = row.original.DeletedAt != null;
  const isUnread = isUnreadGridRow(row.original);
  const isSelectableGrid = Boolean(table.options.enableRowSelection);

  return (
    <TableRow
      ref={rowRef}
      data-index={virtualIndex}
      hover={!isMarkedForDeletion}
      selected={isSelected}
      data-unread={isUnread ? 'true' : undefined}
      onClick={(e) => {
        handleDataGridRowClick(e.target as HTMLElement, row, table, isSelectableGrid);
      }}
      onDoubleClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest('input[type="checkbox"]')) return;
        if (target.closest('td[data-col-id="_selection"]')) return;
        if (target.closest('td[data-col-id="_actions"]')) return;

        const onRowNavigate = table.options.meta?.onRowNavigate;
        if (onRowNavigate) {
          table.setRowSelection({ [row.id]: true });
          onRowNavigate(row.original);
        }
      }}
      style={{
        cursor: isSelectableGrid ? 'pointer' : 'default',
        ...(wordWrap ? { height: 'auto' } : { height: `${rowHeight}px` }),
        opacity: isMarkedForDeletion ? 0.6 : 1,
        textDecoration: isMarkedForDeletion ? 'line-through' : 'none',
        outlineStyle: 'none',
        outlineWidth: '0px',
        outlineOffset: '-2px',
      }}
      sx={{
        backgroundColor: isMarkedForDeletion
          ? 'error.light'
          : isSelected
            ? (theme) =>
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.28)
                  : theme.palette.action.selected
            : isUnread
              ? (theme) => getUnreadRowBackground(theme)
              : rowIndex % 2 !== 0
                ? getStripedRowBackground
                : 'inherit',
        '&.Mui-selected': isMarkedForDeletion ? { backgroundColor: 'error.light' } : {},
        '&.Mui-selected:hover': isMarkedForDeletion ? { backgroundColor: 'error.light' } : {},
        '&:hover': {
          backgroundColor: isMarkedForDeletion
            ? 'error.light'
            : isUnread
              ? (theme) => getUnreadRowBackground(theme, true)
              : (theme) => theme.palette.action.hover,
        },
        color: 'text.primary',
        fontWeight: isUnread ? 650 : undefined,
      }}
    >
      {row
        .getVisibleCells()
        .filter((cell) => !hiddenColumnIds?.has(cell.column.id))
        .map((cell) => {
          const cellKey = `${rowId}:${cell.column.id}`;
          const isCellModified = modifiedCells?.has(cellKey) ?? false;
          const inlineEditable = table.options.meta?.inlineEditable ?? false;
          const rowValidationErrors = inlineEditable ? validationErrors : undefined;
          const hasValidationError = rowValidationErrors?.has(cellKey) ?? false;
          const columnId = cell.column.id;
          const isCenteredCol =
            columnId === '_selection' || columnId === '_actions' || columnId === '_navigate';

          return (
            <TableCell
              key={cell.id}
              data-col-id={columnId}
              data-soft-deleted={isSoftDeleted ? 'true' : undefined}
              style={{
                width: getColumnWidth(cell.column),
                ...(cell.column.columnDef.meta
                  ? {}
                  : {
                      minWidth: `${cell.column.columnDef.minSize || 50}px`,
                      maxWidth: `${cell.column.columnDef.maxSize || 1000}px`,
                    }),
                ...(wordWrap
                  ? {
                      overflow: 'hidden',
                      textOverflow: 'clip',
                      whiteSpace: 'normal',
                      overflowWrap: 'normal',
                      wordBreak: 'normal',
                      verticalAlign: 'top',
                    }
                  : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
                padding: isCenteredCol ? '0 2px' : wordWrap ? '8px 8px 2px' : '2px 8px',
                textAlign: isCenteredCol ? 'center' : undefined,
              }}
              sx={{
                backgroundColor: isMarkedForDeletion
                  ? 'inherit'
                  : isCellModified || isNewRow
                    ? (theme) =>
                        theme.palette.mode === 'dark'
                          ? alpha(theme.palette.info.main, 0.25)
                          : alpha(theme.palette.info.main, 0.12)
                    : 'inherit',
                // Border-only error indicator — no background fill, just a clear red outline.
                ...(hasValidationError && {
                  boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.error.main}`,
                }),
                ...(wordWrap && {
                  '&:has(input[type="checkbox"])': {
                    padding: '2px 8px !important',
                    verticalAlign: 'middle !important',
                  },
                }),
              }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          );
        })}
    </TableRow>
  );
};

// ─── Custom Equality Check for Memoization ───────────────────────────────────

const areRowPropsEqual = <T extends Record<string, unknown>>(
  prevProps: DataRowProps<T>,
  nextProps: DataRowProps<T>
) => {
  if (prevProps.columnOrder !== nextProps.columnOrder) return false;

  if (prevProps.row !== nextProps.row) return false;

  if (prevProps.rowIndex !== nextProps.rowIndex) return false;

  if (prevProps.isSelected !== nextProps.isSelected) return false;

  if (prevProps.columnVisibility !== nextProps.columnVisibility) return false;
  if (prevProps.hiddenColumnIds !== nextProps.hiddenColumnIds) return false;
  if (prevProps.wordWrap !== nextProps.wordWrap) return false;

  const rowId = String(prevProps.row.original.Id || '');
  const prevDeleted = prevProps.markedForDeletion?.has(rowId) ?? false;
  const nextDeleted = nextProps.markedForDeletion?.has(rowId) ?? false;
  if (prevDeleted !== nextDeleted) return false;

  if (prevProps.modifiedCells !== nextProps.modifiedCells) {
    const visibleCells = nextProps.row.getVisibleCells();
    for (let i = 0; i < visibleCells.length; i++) {
      const cellKey = `${rowId}:${visibleCells[i].column.id}`;
      const wasModified = prevProps.modifiedCells?.has(cellKey) ?? false;
      const isModified = nextProps.modifiedCells?.has(cellKey) ?? false;
      if (wasModified !== isModified) return false;
    }
  }

  if (prevProps.validationErrors !== nextProps.validationErrors) {
    const visibleCells = nextProps.row.getVisibleCells();
    for (let i = 0; i < visibleCells.length; i++) {
      const cellKey = `${rowId}:${visibleCells[i].column.id}`;
      const hadError = prevProps.validationErrors?.has(cellKey) ?? false;
      const hasError = nextProps.validationErrors?.has(cellKey) ?? false;
      if (hadError !== hasError) return false;
    }
  }

  return true;
};

export const DataRow = memo(DataRowComponent, areRowPropsEqual) as typeof DataRowComponent;
